import React, { useState, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Input } from '../Input/Input';
import { Button } from '../Button/Button';
import { ArrowUpDown, ChevronUp, ChevronDown, Search } from 'lucide-react';
// Removed Select import since we're using native select elements

export interface TableColumn<T = any> {
  key: string;
  title: string;
  render?: (value: any, record: T, index: number) => React.ReactNode;
  sortable?: boolean;
  searchable?: boolean;
  width?: string;
  align?: 'left' | 'center' | 'right';
}

export interface TableFilter {
  key: string;
  label: string;
  options: { label: string; value: string }[];
}

export interface TableProps<T = any> {
  data: T[];
  columns: TableColumn<T>[];
  loading?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  filters?: TableFilter[];
  pagination?: {
    pageSize?: number;
    showSizeChanger?: boolean;
    pageSizeOptions?: number[];
  };
  className?: string;
  emptyText?: string;
  // Server-side props
  serverSide?: boolean;
  onSearch?: (term: string) => void;
  onFilter?: (filters: Record<string, string>) => void;
  onSort?: (key: string, direction: 'asc' | 'desc') => void;
  onPaginate?: (page: number, pageSize: number) => void;
  totalItems?: number;
}

export function Table<T extends Record<string, any>>({
  data,
  columns,
  loading = false,
  searchable = true,
  searchPlaceholder = 'Search...',
  filters = [],
  pagination = { pageSize: 10, showSizeChanger: true, pageSizeOptions: [5, 10, 20, 50] },
  className,
  emptyText = 'No data available',
  // Server-side props
  serverSide = false,
  onSearch,
  onFilter,
  onSort,
  onPaginate,
  totalItems
}: TableProps<T>) {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(pagination.pageSize || 10);

  // Filter and search data (only for client-side)
  const filteredData = useMemo(() => {
    if (serverSide) {
      return data; // Server handles filtering
    }

    let result = [...data];

    // Apply search
    if (searchTerm && searchable) {
      const searchableColumns = columns.filter(col => col.searchable !== false);
      result = result.filter(item =>
        searchableColumns.some(col => {
          const value = item[col.key];
          return value && String(value).toLowerCase().includes(searchTerm.toLowerCase());
        })
      );
    }

    // Apply filters
    Object.entries(activeFilters).forEach(([filterKey, filterValue]) => {
      if (filterValue) {
        result = result.filter(item => String(item[filterKey]) === filterValue);
      }
    });

    // Apply sorting
    if (sortConfig) {
      result.sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];
        
        if (aVal === bVal) return 0;
        
        const comparison = aVal < bVal ? -1 : 1;
        return sortConfig.direction === 'desc' ? comparison * -1 : comparison;
      });
    }

    return result;
  }, [data, searchTerm, activeFilters, sortConfig, columns, searchable, serverSide]);

  // Pagination
  const itemCount = serverSide ? (totalItems || 0) : filteredData.length;
  const totalPages = Math.ceil(itemCount / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedData = serverSide ? data : filteredData.slice(startIndex, startIndex + pageSize);

  const handleSort = (columnKey: string) => {
    const column = columns.find(col => col.key === columnKey);
    if (!column?.sortable) return;

    const newConfig = (() => {
      if (!sortConfig || sortConfig.key !== columnKey) {
        return { key: columnKey, direction: 'asc' as const };
      }
      if (sortConfig.direction === 'asc') {
        return { key: columnKey, direction: 'desc' as const };
      }
      return null;
    })();

    setSortConfig(newConfig);
    
    if (serverSide && onSort && newConfig) {
      onSort(newConfig.key, newConfig.direction);
    }
  };

  const handleFilterChange = (filterKey: string, value: string) => {
    const newFilters = { ...activeFilters, [filterKey]: value };
    setActiveFilters(newFilters);
    setCurrentPage(1);
    
    if (serverSide && onFilter) {
      onFilter(newFilters);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
    
    if (serverSide && onSearch) {
      onSearch(value);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    
    if (serverSide && onPaginate) {
      onPaginate(page, pageSize);
    }
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
    
    if (serverSide && onPaginate) {
      onPaginate(1, newPageSize);
    }
  };

  const renderCell = (column: TableColumn<T>, record: T, index: number) => {
    if (column.render) {
      return column.render(record[column.key], record, index);
    }
    return record[column.key];
  };

  const getSortIcon = (columnKey: string) => {
    if (!sortConfig || sortConfig.key !== columnKey) {
      return <ArrowUpDown className="w-4 h-4 opacity-50" />;
    }

    if (sortConfig.direction === 'asc') {
      return <ChevronUp className="w-4 h-4" />;
    }

    return <ChevronDown className="w-4 h-4" />;
  };

  return (
    <div className={cn('w-full space-y-4', className)}>
      {/* Search and Filters */}
      {(searchable || filters.length > 0) && (
        <div className="flex flex-col sm:flex-row gap-4">
          {searchable && (
            <div className="flex-1">
              <Input
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                leftIcon={<Search className="w-4 h-4" />}
              />
            </div>
          )}
          
          {filters.map(filter => (
            <div key={filter.key} className="min-w-[200px]">
              <select
                value={activeFilters[filter.key] || ''}
                onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="">{filter.label}</option>
                {filter.options.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
      )}

      {/* Table */}
      <div className="border rounded-lg overflow-hidden bg-background">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50">
                {columns.map(column => (
                  <th
                    key={column.key}
                    className={cn(
                      'px-4 py-3 text-left text-sm font-medium text-muted-foreground',
                      column.sortable && 'cursor-pointer hover:text-foreground select-none',
                      column.align === 'center' && 'text-center',
                      column.align === 'right' && 'text-right'
                    )}
                    style={{ width: column.width }}
                    onClick={() => column.sortable && handleSort(column.key)}
                  >
                    <div className="flex items-center gap-2">
                      <span>{column.title}</span>
                      {column.sortable && getSortIcon(column.key)}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={columns.length} className="px-4 py-8 text-center text-muted-foreground">
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-muted border-t-foreground rounded-full animate-spin" />
                      Loading...
                    </div>
                  </td>
                </tr>
              ) : paginatedData.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="px-4 py-8 text-center text-muted-foreground">
                    {emptyText}
                  </td>
                </tr>
              ) : (
                paginatedData.map((record, index) => (
                  <tr key={index} className="border-b hover:bg-muted/30 transition-colors">
                    {columns.map(column => (
                      <td
                        key={column.key}
                        className={cn(
                          'px-4 py-3 text-sm',
                          column.align === 'center' && 'text-center',
                          column.align === 'right' && 'text-right'
                        )}
                      >
                        {renderCell(column, record, startIndex + index)}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {pagination && totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {startIndex + 1}-{Math.min(startIndex + pageSize, itemCount)} of {itemCount} entries
          </div>
          
          <div className="flex items-center gap-2">
            {pagination.showSizeChanger && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Show:</span>
                <select
                  value={String(pageSize)}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  className="flex h-8 w-20 items-center justify-between rounded-md border border-input bg-background px-2 py-1 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                >
                  {(pagination.pageSizeOptions || [5, 10, 20, 50]).map(size => (
                    <option key={size} value={String(size)}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
              >
                First
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              
              <span className="px-3 py-1 text-sm">
                Page {currentPage} of {totalPages}
              </span>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
              >
                Last
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
